











import { BehaviorSubject } from 'rxjs';
import { Component, Vue } from 'vue-property-decorator';

import Player from '@/components/Player.vue';
import StartTemplate from '@/components/templates/StartTemplate.vue'; // @ is an alias to /src
import ErrorTemplate from '@/components/templates/ErrorTemplate.vue';
import StopLiveTemplate from '@/components/templates/StopLiveTemplate.vue'; // @ is an alias to /src
import ErrorPlayerTemplate from '@/components/templates/ErrorPlayerTemplate.vue'; // @ is an alias to /src

import { etvService } from './services/etv.service';
import { configService } from './services/config.service';
import { statisticService } from './services/statistic.service';
import { logService, LogLevel } from './services/log.service';
import { iFrameService } from './services/iframe.service';

import { PlayerConfig, AvailableLanguages, EtvConfig } from '@/interfaces/player-config.interface';
const version = require('../package.json').version;

export enum Template {
  START = 'START',
  ERROR = 'ERROR',
  STOP = 'STOP',
  PLAYER_ERROR = 'PLAYER_ERROR',
  PLAYER = 'PLAYER',
  NOTHING = 'NOTHING',
}

@Component({
  components: {
    StartTemplate,
    ErrorTemplate,
    StopLiveTemplate,
    ErrorPlayerTemplate,
    Player,
  },
})
export default class App extends Vue {
  public template: Template = Template.NOTHING;
  public account: string = '';

  private userConfig: any;
  private playerConfig!: PlayerConfig;

  private ready: boolean = false;

  private parentUrl = '';

  private beforeCreate() {
    this.userConfig = this.$route.query;

    if (this.userConfig.loglevel) {
      logService.init(this.userConfig.loglevel);
    } else {
      logService.init(process.env.VUE_APP_LOG_LEVEL as LogLevel);
    }

    if (process.env.NODE_ENV === 'production') {
      logService.info(
        `\n\n------ ETV PLAYER VERSION: ${version} ------\n` +
          `• MODE: ${process.env.NODE_ENV}\n` +
          `• LOG-LEVEL: ${process.env.VUE_APP_LOG_LEVEL}\n` +
          `---------------------------------------\n\n`
      );
    } else {
      logService.info(
        `\n\n------ ETV PLAYER VERSION: ${version} ------\n` +
          `• MODE: ${process.env.NODE_ENV}\n` +
          `• API PATH: ${process.env.VUE_APP_API_PATH}\n` +
          `• LOG-LEVEL: ${process.env.VUE_APP_LOG_LEVEL}\n` +
          `• REPROTING ENABLED: ${process.env.VUE_APP_REPORTING_ENABLED}\n` +
          `• REPORTING PATH: ${process.env.VUE_APP_REPORTING_PATH}\n` +
          `---------------------------------------\n\n`
      );
    }
  }

  private async created() {
    if (this.inIframe()) {
      this.parentUrl = await iFrameService.handshake();
    } else {
      this.parentUrl = location.host;
    }

    const isValid = await configService.validateQuery(this.userConfig);
    if (isValid) {
      this.getETVConfig(this.userConfig.token);
    } else {
      logService.warn('Query not valid');
      this.setTemplate(Template.ERROR);
    }
  }

  private inIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  private getETVConfig(token: any) {
    etvService
      .init(token)
      .then(async (configRef: any) => {
        this.subscribeToETV(configRef);
      })
      .catch((e: Error) => {
        logService.error('getETVConfig', e);
        this.setTemplate(Template.ERROR);
      });
  }

  private async subscribeToETV(configRef: any) {
    configRef.subscribe(async (etvConfig: EtvConfig) => {
      try {
        this.playerConfig = await configService.getConfig(this.parentUrl, this.userConfig, etvConfig);
        logService.trace('etvConfig update: ', this.playerConfig);
        this.account = 'acc-' + etvConfig.template.accountId;

        await this.setConfig(etvConfig);
        this.updateConfig(etvConfig);

        if (this.playerConfig.maintenance) {
          logService.warn('maintenance mode is active');
          this.setTemplate(Template.ERROR);
        } else if (this.template === Template.NOTHING || this.template === Template.ERROR) {
          this.playerConfig.autoplay ? this.setTemplate(Template.PLAYER) : this.setTemplate(Template.START);
        }
      } catch (error) {
        this.setTemplate(Template.ERROR);
        console.error(error);
      }
    });
  }

  private async setConfig(etvConfig: EtvConfig) {
    if (this.ready) {
      return;
    }
    this.ready = true;

    if (etvConfig.template.analytics.google) {
      statisticService.initGoogleAnalytics(etvConfig.template.analytics.google, etvConfig);
    }

    if (process.env.VUE_APP_REPORTING_ENABLED !== 'false') {
      statisticService.initReporting();
    }

    if (this.playerConfig.advertisment.enabled) {
      try {
        await (Vue as any).loadScript('//imasdk.googleapis.com/js/sdkloader/ima3.js');
      } catch (error) {
        this.playerConfig.advertisment.enabled = false;
      }
    }

    iFrameService.initParent(this.playerConfig);
  }

  /**
   * update config in all relevant services
   */
  private updateConfig(etvConfig: EtvConfig) {
    statisticService.updateConfig(etvConfig);
  }

  private setTemplate(template: Template) {
    logService.info('setTemplate: ' + template);
    this.template = template;
  }

  private async reloadPlayer() {
    etvService
      .reload()
      .then(() => {
        this.playerConfig.autoplay = true;
        setTimeout(() => {
          this.setTemplate(Template.PLAYER);
        }, 10);
      })
      .catch(() => {
        this.setTemplate(Template.ERROR);
      });
  }
}
